import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { put, get } from 'api/fetch';
import { PAYMENT_STATUS } from 'products/nomad_insurance/constants';

export const updatePaymentStatus = createAsyncThunk(
  'nomad_insurance/purchaseInsurance/independencePayment/paymentStatus',
  async (contractId, { rejectWithValue }) => {
    try {
      const res = await put(`/contracts/v1/${contractId}/payment-status`, null, {
        isNIOrchestrator: true
      });
      return res;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getPaymentStatus = createAsyncThunk(
  'nomad_insurance/purchaseInsurance/independencePayment/paymentStatus',
  async (applicationBundleId, { rejectWithValue }) => {
    try {
      const res = await get(
        `/contracts/v2/payment-status?applicationBundleId=${applicationBundleId}`,
        {
          isNIOrchestrator: true
        }
      );
      return res.paymentStatus;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// ================== Reducers ================== //

const slice = createSlice({
  name: 'nomad_insurance/purchaseInsurance/independencePayment/paymentStatus',
  initialState: {
    isLoading: false,
    error: null,
    paymentStatusUpdated: false,
    getPaymentStatus: {
      isLoading: false,
      error: null,
      paymentStatus: null
    },
    pollPaymentStatus: {
      isPolling: false,
      isTimedOut: false,
      isSucceeded: false,
      isFailed: false
    }
  },
  reducers: {
    // pollPaymentStatus
    pollPaymentStatusRequest: (state) => {
      state.pollPaymentStatus.isPolling = true;
      state.pollPaymentStatus.isTimedOut = false;
      state.pollPaymentStatus.isSucceeded = false;
      state.pollPaymentStatus.isFailed = false;
    },
    pollPaymentStatusSuccess: (state, { payload }) => {
      state.pollPaymentStatus.isPolling = false;
      if (payload === PAYMENT_STATUS.ACTIVE) {
        state.pollPaymentStatus.isSucceeded = true;
      }
      if (payload === PAYMENT_STATUS.PAYMENT_FAILED) {
        state.pollPaymentStatus.isFailed = true;
      }
    },
    pollPaymentStatusFailure: (state) => {
      state.pollPaymentStatus.isPolling = false;
      state.pollPaymentStatus.isTimedOut = true;
    }
  },
  extraReducers: {
    [updatePaymentStatus.pending]: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    [updatePaymentStatus.fulfilled]: (state) => {
      state.isLoading = false;
      state.error = null;
      state.paymentStatusUpdated = true;
    },
    [updatePaymentStatus.rejected]: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
    },
    [getPaymentStatus.pending]: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    [getPaymentStatus.fulfilled]: (state, { payload }) => {
      state.isLoading = false;
      state.error = null;
      state.getPaymentStatus.paymentStatus = payload;
    },
    [getPaymentStatus.rejected]: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
    }
  }
});

export default slice.reducer;

// ================== Actions ================== //

const { actions } = slice;

// eslint-disable-next-line no-promise-executor-return
const timeout = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// poll payment status for 60 seconds max to get the final payment status
// which is communicated from Stripe via webhook to our backend, which then changes the payment status
// once the status is ACTIVE or PAYMENT_FAILED, we know we have the final status and can stop polling
// after 60 seconds we stop polling and show an error message, even if we got no conclusive result
export const pollPaymentStatus =
  (contractId, started = Date.now()) =>
  async (dispatch, getState) => {
    if (started + 60000 < Date.now()) {
      dispatch(actions.pollPaymentStatusFailure());
      return;
    }

    dispatch(actions.pollPaymentStatusRequest());

    await dispatch(getPaymentStatus(contractId));

    const { paymentStatus } =
      getState().nomadInsurance.purchaseInsurance.independencePayment.paymentStatus
        .getPaymentStatus;

    if (
      paymentStatus === PAYMENT_STATUS.ACTIVE ||
      paymentStatus === PAYMENT_STATUS.PAYMENT_FAILED
    ) {
      dispatch(actions.pollPaymentStatusSuccess(paymentStatus));
      return;
    }

    await timeout(3000);

    dispatch(pollPaymentStatus(contractId, started));
  };
